import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@structural-climbing/ui'
import { LEGAL_STATEMENT_MAX_LENGTH, LEGAL_STATEMENTS_MAX } from '@structural-climbing/validation'

export interface LegalStatement {
  /** Absent on a statement added in this session; the Worker assigns it. */
  id?: string
  text: string
  required: boolean
}

interface Props {
  document: { id: string; title: string; statements: LegalStatement[] }
  onClose: () => void
  onSaved: () => void
}

// A row needs a stable key while its text is being typed, and a new statement
// has no id yet.
interface Row extends LegalStatement {
  key: string
}

let nextKey = 0
const toRow = (statement: LegalStatement): Row => ({ ...statement, key: `row-${nextKey++}` })

/**
 * The statements a signer ticks, one by one, before signing a document.
 *
 * A gym's lawyer asked for each declaration to be accepted on its own, with a
 * box the signer ticks themselves. The gym writes them here. They are part of
 * what is signed, so saving a change makes a new version of the document and
 * everyone signs again; the modal says so before the owner saves.
 */
export function LegalStatementsModal({ document, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()

  const [rows, setRows] = useState<Row[]>(() => document.statements.map(toRow))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = (key: string, patch: Partial<LegalStatement>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  const hasEmpty = rows.some((row) => !row.text.trim())
  const tooLong = rows.some((row) => row.text.trim().length > LEGAL_STATEMENT_MAX_LENGTH)

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${import.meta.env.VITE_API_URL as string}/legal-documents/${document.id}/statements`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            statements: rows.map(({ id, text, required }) => ({
              ...(id ? { id } : {}),
              text: text.trim(),
              required,
            })),
          }),
        },
      )
      if (!res.ok) {
        setError(t('legal.errorStatements'))
        return
      }
      onSaved()
      onClose()
    } catch {
      setError(t('legal.errorStatements'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{t('legal.statementsTitle', { title: document.title })}</ModalHeader>
        <ModalBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 text-sm text-foreground/70">
            <p>{t('legal.statementsIntro')}</p>
            <p>{t('legal.statementsOptionalHint')}</p>
          </div>

          {rows.length === 0 && (
            <p className="py-2 text-sm text-foreground/40">{t('legal.statementsEmpty')}</p>
          )}

          {rows.map((row, index) => (
            <div key={row.key} className="flex flex-col gap-2 rounded-lg border border-divider p-3">
              <Textarea
                label={t('legal.statementLabel', { number: index + 1 })}
                value={row.text}
                onValueChange={(text) => update(row.key, { text })}
                minRows={2}
                isDisabled={saving}
                isInvalid={row.text.trim().length > LEGAL_STATEMENT_MAX_LENGTH}
                errorMessage={t('legal.statementTooLong', { max: LEGAL_STATEMENT_MAX_LENGTH })}
              />
              <div className="flex items-center justify-between gap-3">
                <Checkbox
                  isSelected={row.required}
                  isDisabled={saving}
                  onValueChange={(required) => update(row.key, { required })}
                >
                  {t('legal.statementRequired')}
                </Checkbox>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isDisabled={saving}
                  aria-label={t('legal.removeStatementNumber', { number: index + 1 })}
                  onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                >
                  {t('legal.removeStatement')}
                </Button>
              </div>
            </div>
          ))}

          <Button
            size="sm"
            variant="flat"
            className="self-start"
            isDisabled={saving || rows.length >= LEGAL_STATEMENTS_MAX}
            onClick={() => setRows((current) => [...current, toRow({ text: '', required: true })])}
          >
            {t('legal.addStatement')}
          </Button>

          <p className="text-sm font-medium text-warning-600">
            {t('legal.statementsResignWarning')}
          </p>
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" isDisabled={saving} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            isLoading={saving}
            isDisabled={saving || hasEmpty || tooLong}
            onClick={() => void handleSave()}
          >
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
